import { IProject } from "Dashboard/scenes/Projects/domain/IProject";
import { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { projectService } from "Dashboard/scenes/Projects/services/project.service";
import { useAppDispatch } from "hooks/hooks";
import {
  selectState,
  setDeleteProjectModalOpen,
  setEditNotePopoverOpen,
  setProjectField,
  setSelectedIndex,
  updateProject,
} from "../main.slice";
import { DiffDays, FormatDateIso, FormatDateShort } from "utils/dateUtils";
import { useSelector } from "react-redux";
import deleteIcon from "./../../../../../images/delete-icon.png";
import InfoTooltip from "components/Popup/InfoTooltip";
import { IUpdateProjectRequestParams } from "Dashboard/scenes/Projects/domain/IUpdateProjectRequestParams";
import DatePicker from "react-datepicker";
import { IOption } from "Dashboard/domain/IOption";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { PROJECT_MODULE_NAME } from "Dashboard/scenes/Projects/domain/ProjectModule";
import NumberInput from "Dashboard/components/NumberInput";
import { toast } from "react-toastify";
import EmployeeMultiSelect from "Dashboard/scenes/Projects/components/EmployeeMultiSelect";
import { stylesMulti } from "../../../components/SelectStyle";
import ProjectCategorySelect from "Dashboard/scenes/Projects/components/ProjectCategorySelect";
import ProjectTypeSelectCreate from "Dashboard/scenes/Projects/components/ProjectTypeSelectCreate";
import { styles } from "Dashboard/components/SelectStyle";

interface IProps {
  project: IProject;
  employees: IOption[];
  index: number;
};

const ProjectRow = ({ project, employees, index }: IProps) => {

  const dispatch = useAppDispatch();
  const { selectedIndex } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const projectManagersAsString = (project.managers
    && project.managers.length > 0 && project.managers.map(manager => manager.name).join(", "))
    || undefined;

  const delayedProjectUpdate = useMemo(
    () =>
      debounce(async (data: IUpdateProjectRequestParams) => {
        try {
          const project = await projectService.updateProject(data);
          dispatch(updateProject({ index, project }));
          toast.info("Projekat je uspešno izmenjen");
        } catch (error) {
          console.log(error);
        }
      }, 1000),
    [dispatch, index]
  );

  const onProjectValueChange = (newProjectValue: number) => {
    delayedProjectUpdate({
      projectId: project.projectId,
      projectValue: newProjectValue,
      forcedField: "projectValue",
    });
  };

  const onInputCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    delayedProjectUpdate({
      projectId: project.projectId,
      [event.target.name]: event.target.checked,
    });
  };

  const onDateChange = async (dateField: string, date: Date | null) => {
    const dateToString = date ? FormatDateIso(date) : null;
    dispatch(setProjectField({
      projectId: project.projectId,
      [dateField]: dateToString
    }))
    delayedProjectUpdate({
      projectId: project.projectId,
      [dateField]: dateToString,
      forcedField: dateField,
    });
  };

  const onChangeManager = async (newManagerIds: number[]) => {
    delayedProjectUpdate({
      projectId: project.projectId,
      managerIds: newManagerIds
    });
  };

  const [categoryId, setCategoryId] = useState(project.category?.categoryId);
  const [typeId, setTypeId] = useState(project.type?.typeId);

  useEffect(() => {
    setCategoryId(project.category?.categoryId);
  }, [project.category?.categoryId]);

  useEffect(() => {
    setTypeId(project.type?.typeId);
  }, [project.type?.typeId]);

  const onCategoryChange = (newCategoryId: number | null) => {
    if (newCategoryId === null) {
      return;
    }
    setCategoryId(newCategoryId);
    delayedProjectUpdate({
      projectId: project.projectId,
      categoryId: newCategoryId,
      forcedField: "categoryId",
    });
  };

  const onTypeChange = (newTypeId: number | null) => {
    if (newTypeId === null) {
      return;
    }
    setTypeId(newTypeId);
    delayedProjectUpdate({
      projectId: project.projectId,
      typeId: newTypeId,
      forcedField: "typeId",
    });
  };

  return (
    <div
      className={`row${index === selectedIndex ? " active" : ""}`}
      onClick={() => { dispatch(setSelectedIndex(index)) }}
    >
      <div className="col center">
        <input
          name="isFavourite"
          type="checkbox"
          onClick={(e) => e.stopPropagation()}
          onChange={onInputCheckboxChange}
          defaultChecked={!!project.isFavourite}
          disabled={!hasWriteAccessTo(user, PROJECT_MODULE_NAME)}
        />
      </div>
      <InfoTooltip tooltip={project.projectName} classesProp="col strong left projectName">
        <div className="bg-item">{project.projectName}</div>
      </InfoTooltip>
      {hasFullAccessTo(user, PROJECT_MODULE_NAME)
        ? <InfoTooltip tooltip="Obriši zapis" classesProp="col delete-icon-div">
          <button
            onClick={(e) => {
              e.stopPropagation();
              dispatch(setDeleteProjectModalOpen({ visible: true, index }));
            }}
          >
            <img className="delete-icon" src={deleteIcon} alt="" />
          </button>
        </InfoTooltip> : <div></div>}
      <div className="col light">
        {hasFullAccessTo(user, PROJECT_MODULE_NAME) ? (
          <div className="bg-item bg-item-border-light overflowMinWidth center">
            <ProjectCategorySelect
              value={categoryId}
              onChange={onCategoryChange}
              customStyles={styles.dropdownInTable}
            />
          </div>
        ) : (
          <div className="bg-item bg-item-border-light overflowMinWidth center">
            <InfoTooltip
              tooltip={project.category?.name}
              classesProp="bg-text-overflow">
              {project.category?.name}
            </InfoTooltip>
          </div>
        )}
      </div>
      <div className="col center light">
        {hasFullAccessTo(user, PROJECT_MODULE_NAME) ? (
          <div className="bg-item-border-light">
            <ProjectTypeSelectCreate
              value={typeId}
              onChange={onTypeChange}
              customStyles={styles.dropdownInTable}
            />
          </div>
        ) : (
          <div className="bg-item-border-light">{project.type?.name}</div>
        )}
      </div>
      <div className="col light">
        <div className="bg-item bg-item-border-light overflowMinWidth center">
          <InfoTooltip
            tooltip={project.municipality?.name}
            classesProp="bg-text-overflow">
            {project.municipality?.name}
          </InfoTooltip>
        </div>
      </div>
      <div className="col left light">
        <div className="bg-item-border-light">{project.client?.shortName}</div>
      </div>
      <div className="col center">
        <NumberInput
          formatAs="rsd"
          className="bg-item-border-dotted center"
          defaultValue={`${project.projectValue}`}
          onChange={(_: string, __: string, parsedValue: number) => {
            if (parsedValue !== project.projectValue) {
              onProjectValueChange(parsedValue);
            }
          }}
          disabled={!hasWriteAccessTo(user, PROJECT_MODULE_NAME)}
        />
      </div>
      <div className="col left light">
        <div className="bg-item-border-light"
          onClick={(event) => {
            event.stopPropagation();
            dispatch(setEditNotePopoverOpen({
              popover: { isOpen: true, left: event.pageX, top: event.pageY },
              projectId: project.projectId,
              projectIndex: index,
              note: project.note,
            }));
          }}>
          ?
        </div>
      </div>
      <div className="col center light strong">
        <div className="bg-item-border-dotted">
          {project.contractDate && FormatDateShort(project.contractDate)}
        </div>
      </div>
      <div className="col center light strong">
        <div className="bg-item-border-dotted">
          {project.deadlineForCompletion && FormatDateShort(project.deadlineForCompletion)}
        </div>
      </div>
      <div className="col center light">
        <div className="bg-item-border-dotted">
          {hasWriteAccessTo(user, PROJECT_MODULE_NAME) ?
            <DatePicker
              popperProps={{ strategy: "fixed" }}
              dateFormat="dd.MM.yy"
              showMonthDropdown
              showYearDropdown
              onChange={(date) => onDateChange("startDate", date)}
              selected={project.startDate ? new Date(project.startDate) : null}
              isClearable={false}
              className="center light"
            /> : <>
              {project.startDate && FormatDateShort(project.startDate)}</>}
        </div>
      </div>
      <div className="col center light">
        <div className="bg-item-dark">
          {hasWriteAccessTo(user, PROJECT_MODULE_NAME)
            ? <DatePicker
              popperProps={{ strategy: "fixed" }}
              dateFormat="dd.MM.yy"
              showMonthDropdown
              showYearDropdown
              onChange={(date) => onDateChange("finalDeadline", date)}
              selected={project.finalDeadline
                ? new Date(project.finalDeadline) : null}
              isClearable={false}
              className="center light"
            />
            : <>{project.finalDeadline && FormatDateShort(project.finalDeadline)}</>}
        </div>
      </div>
      <div className="col center strong">
        <div className="bg-item-border-dotted">
          {project.finalDeadline && DiffDays(project.finalDeadline) + " dana"}
        </div>
      </div>
      <div className="col center">
        <div className="bg-item-border-dotted light">
          {hasWriteAccessTo(user, PROJECT_MODULE_NAME)
            ? <DatePicker
              popperProps={{ strategy: "fixed" }}
              dateFormat="dd.MM.yy"
              showMonthDropdown
              showYearDropdown
              onChange={(date) => onDateChange("finishDate", date)}
              selected={project.finishDate ? new Date(project.finishDate) : null}
              isClearable={false}
              className="center light"
            />
            : <>{project.finishDate && FormatDateShort(project.finishDate)}</>}
        </div>
      </div>
      <InfoTooltip tooltip="Prikaži na mobilnom uređaju"
        classesProp="col center">
        <input
          disabled={!hasWriteAccessTo(user, PROJECT_MODULE_NAME)}
          name="forMobileApp"
          type="checkbox"
          onClick={(e) => e.stopPropagation()}
          onChange={onInputCheckboxChange}
          defaultChecked={!!project.forMobileApp}
        />
      </InfoTooltip>
      <InfoTooltip
        tooltip="Čekirane projekte ne vide padajući meniji publishera"
        classesProp="col center"
      >
        <input
          name="hideInDropDowns"
          type="checkbox"
          onClick={(e) => e.stopPropagation()}
          onChange={onInputCheckboxChange}
          defaultChecked={!!project.hideInDropDowns}
          disabled={!hasWriteAccessTo(user, PROJECT_MODULE_NAME)}
        />
      </InfoTooltip>
      {hasWriteAccessTo(user, PROJECT_MODULE_NAME)
        ? <div className="col">
          <InfoTooltip
            tooltip={projectManagersAsString}
            classesProp="bg-item bg-item-border-light">
            <EmployeeMultiSelect
              value={project.managers}
              options={employees}
              onChange={onChangeManager}
              customStyles={stylesMulti.customMultiSelectStyle}
            />
          </InfoTooltip>
        </div>
        : <div className="col light">
          <div className="bg-item bg-item-border-light overflowMinWidth">
            <InfoTooltip
              tooltip={projectManagersAsString}
              classesProp="bg-text-overflow">
              {projectManagersAsString}
            </InfoTooltip>
          </div>
        </div>}
    </div>
  );
};

export default ProjectRow;
